import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '@/lib/apiFetch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link2, ExternalLink, Unplug, RefreshCw, MapPin, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  active: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  trial: { label: 'Trial', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
  paused: { label: 'Paused', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
  expired: { label: 'Expired', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
  uninstalled: { label: 'Uninstalled', color: 'bg-[#27272A] text-[#71717A] border-[#27272A]' },
};

export default function GHLSetup({ onSettingsChange }) {
  const [oauthStatus, setOauthStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState('');
  const connectTimeoutRef = useRef(null);

  useEffect(() => {
    fetchOAuthStatus();
    // Check for OAuth callback params in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth_success') === 'true') {
      // Clean URL and refresh
      window.history.replaceState({}, '', '/');
      toast.success('CRM connected successfully');
      fetchOAuthStatus();
      onSettingsChange?.();
    } else if (params.get('oauth_error')) {
      const errCode = params.get('oauth_error');
      const msg = `OAuth connection failed: ${errCode}`;
      setError(msg);
      toast.error(msg);
      window.history.replaceState({}, '', '/');
    }

    // Safety: reset connecting state if user returns via browser back/bfcache
    const onPageShow = (e) => {
      if (e.persisted) setConnecting(false);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') setConnecting(false);
    };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
      if (connectTimeoutRef.current) clearTimeout(connectTimeoutRef.current);
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOAuthStatus = async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/api/oauth/status');
      setOauthStatus(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setError('');
    // Safety timeout: if we haven't navigated away in 10s, something went wrong (popup blocked, ad blocker)
    if (connectTimeoutRef.current) clearTimeout(connectTimeoutRef.current);
    connectTimeoutRef.current = setTimeout(() => setConnecting(false), 10000);
    try {
      const { data } = await apiFetch('/api/oauth/authorize');
      if (data?.authorize_url) {
        window.location.assign(data.authorize_url);
        // If we're still here after a beat, the navigation didn't take — reset the button
        setTimeout(() => setConnecting(false), 3000);
      } else {
        setError('GHL OAuth is not configured. Please contact support.');
        toast.error('GHL OAuth is not configured.');
        setConnecting(false);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to start OAuth';
      setError(msg);
      toast.error(msg);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError('');
    try {
      await apiFetch('/api/oauth/disconnect', { method: 'POST' });
      await fetchOAuthStatus();
      onSettingsChange?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const isConnected = oauthStatus?.connected;
  const subStatus = oauthStatus?.subscription_status || '';
  const statusInfo = STATUS_LABELS[subStatus] || { label: subStatus || 'Unknown', color: 'bg-[#27272A] text-[#71717A] border-[#27272A]' };

  return (
    <div className="border border-[#27272A] rounded-lg bg-[#1A1A1A] p-5 transition-all duration-200 hover:border-[#7B2FFF]/30">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-[#7B2FFF]" />
          <h3 className="text-sm font-semibold text-[#EDEDF0]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            CRM Integration
          </h3>
        </div>
        {isConnected && (
          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs" data-testid="ghl-connected-badge">
            Connected
          </Badge>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="w-5 h-5 border-2 border-[#7B2FFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : isConnected ? (
        /* Connected state */
        <div className="space-y-3">
          {/* Location ID */}
          <div className="flex items-center gap-2 p-3 rounded-md bg-[#111111] border border-[#27272A]">
            <MapPin className="w-4 h-4 text-[#A1A1AA]" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[#71717A]">Location ID</p>
              <p className="text-sm text-[#EDEDF0] font-mono truncate" data-testid="ghl-location-id">
                {oauthStatus.location_id}
              </p>
            </div>
          </div>

          {/* Subscription status */}
          <div className="flex items-center gap-2 p-3 rounded-md bg-[#111111] border border-[#27272A]">
            <CreditCard className="w-4 h-4 text-[#A1A1AA]" />
            <div className="flex-1">
              <p className="text-xs text-[#71717A]">Subscription</p>
            </div>
            <Badge className={`text-xs ${statusInfo.color}`} data-testid="subscription-status-badge">
              {statusInfo.label}
            </Badge>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              data-testid="refresh-oauth-status"
              onClick={fetchOAuthStatus}
              variant="outline"
              size="sm"
              className="flex-1 border-[#27272A] bg-transparent text-[#A1A1AA] hover:text-[#EDEDF0] hover:bg-[#ffffff08] hover:border-[#7B2FFF]/40 rounded-md h-9 text-sm"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
            <Button
              data-testid="disconnect-ghl-button"
              onClick={handleDisconnect}
              disabled={disconnecting}
              variant="outline"
              size="sm"
              className="border-[#27272A] bg-transparent text-[#A1A1AA] hover:text-red-400 hover:bg-red-400/5 hover:border-red-400/30 rounded-md h-9 text-sm px-4"
            >
              {disconnecting ? (
                <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <><Unplug className="w-3.5 h-3.5 mr-1.5" /> Disconnect</>
              )}
            </Button>
          </div>
        </div>
      ) : (
        /* Disconnected state — show connect button */
        <div className="space-y-4">
          <p className="text-sm text-[#A1A1AA] leading-relaxed">
            Connect your CRM account via OAuth to enable AI-powered message automation.
          </p>

          {error && (
            <div className="p-2.5 border border-red-500/20 rounded-md bg-red-500/5 text-red-400 text-xs">
              {error}
            </div>
          )}

          <Button
            data-testid="connect-ghl-button"
            onClick={handleConnect}
            disabled={connecting}
            className="w-full bg-[#7B2FFF] hover:bg-[#9353FF] text-white font-medium rounded-md h-10 text-sm transition-all duration-200 hover:shadow-[0_0_16px_rgba(123,47,255,0.25)] active:scale-[0.98]"
          >
            {connecting ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <span className="flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                Connect your CRM
              </span>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
